import { WsEventType, WsMessageType, type WsBaseMessage, type WsSaconProductMessage } from '@/shared/realtime/ws-types';
import type { VersionedRuntimeEntity } from './normalized-store';
import { isStructuralFloodSensor } from './sensor-presentation';
import { warningDisplayKey } from './product-warning-clear';

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/** 비어 있거나 미등록으로 표시된 수령인 이름은 알림의 우선 이름으로 사용하지 않습니다. */
function readRecipientName(value: unknown): string {
  return typeof value === 'string' && value.trim() !== '-' ? value.trim() : '';
}

/** 확정된 센서 연결만 기존 카드의 표시 계약으로 변환합니다. 이름으로 장비를 추정하지 않습니다. */
export function productSensorMessages(product: VersionedRuntimeEntity, dismissedSensors?: Readonly<Record<string, string>>): WsSaconProductMessage[] {
  if (!Array.isArray(product.sensors)) return [];
  return product.sensors.flatMap(value => {
    const sensor = record(value);
    const id = typeof sensor.source_device_uid === 'string' ? sensor.source_device_uid : typeof sensor.id === 'string' ? sensor.id : '';
    if (!id) return [];
    // 초기 센서 조회와 같은 구성 관계로 이름을 만들고 설치 해제의 명시적 null도 전달합니다.
    const component = (Array.isArray(product.components) ? product.components : []).map(record).find(component => (component.product_unit_id ?? component.id) === sensor.id);
    const deviceName = sensor.device_name ?? (component?.role === 'COMPONENT' && sensor.sensor_model_name ? `${product.name} / ${sensor.sensor_model_name}` : product.name);
    const dto = record(record(sensor.attributes).dto);
    const metrics = Object.fromEntries(
      (Array.isArray(sensor.metrics) ? sensor.metrics : []).map(value => {
        const metric = record(value);
        return [String(metric.code).toLowerCase(), metric.value];
      })
    );
    const online = sensor.availability === 'ONLINE' || sensor.availability === 'DEGRADED';
    const displayStatus = dismissedSensors?.[String(sensor.id)] === warningDisplayKey(sensor) ? 'NORMAL' : sensor.display_status;
    const flooded = sensor.rx_profile_code === 'SAFETY_GUARD_MINI_FLOODING' && dto.sensorState === 'F3' && ['ALARM', 'CRITICAL'].includes(String(displayStatus));
    const state = flooded ? 'ALERT' : online ? String(dto.state ?? 'NORMAL') : sensor.availability === 'OFFLINE' ? 'OFF' : 'UNKNOWN';
    const timestamp = String((online ? sensor.last_observed_at : sensor.projected_at) ?? product.projected_at ?? product.last_observed_at ?? '');
    return [
      {
        type: WsMessageType.DEVICE,
        event: state === 'ALERT' ? WsEventType.SACON_PRODUCT_ALERT : state === 'WARNING' ? WsEventType.SACON_PRODUCT_WARNING : WsEventType.SACON_PRODUCT_PROCESSED,
        key: `${sensor.id}:${sensor.version}:${product.version}`,
        sent_at: timestamp,
        topic: 'v2.iot.projection',
        field_uid: product.field_uid,
        body: {
          ...dto,
          ...metrics,
          provider: 'V2',
          iot_ref: { provider: 'V2', fieldUid: product.field_uid, kind: 'SENSOR', id: sensor.id },
          field_uid: product.field_uid,
          field_name: product.field_name,
          device_uid: id,
          source_device_uid: id,
          display_device_uid: product.id ?? product.entity_id,
          product_unit_id: sensor.id,
          rx_profile_code: sensor.rx_profile_code,
          is_structural_flood: isStructuralFloodSensor(sensor),
          product_instance_id: product.id,
          device_name: deviceName,
          install_location_text: Object.hasOwn(product, 'install_location_text') ? product.install_location_text : product.location,
          device_code: sensor.device_code ?? product.instance_code,
          product_sensor_type: sensor.product_sensor_type,
          product_lora_type: sensor.product_lora_type,
          r_id: sensor.receiver_address,
          s_id: sensor.sensor_address,
          device_group_name: record(product.group).name,
          state,
          device_state: online ? product.display_status : sensor.availability,
          // 제품 대표와 센서 UID가 달라도 실제 센서의 수신 복구·끊김 상태를 갱신합니다.
          source_device_state: flooded ? 'ALARM' : online ? String(displayStatus ?? 'UNKNOWN') : sensor.availability === 'OFFLINE' ? 'OFF' : 'UNKNOWN',
          availability: sensor.availability,
          // 통신 상태 갱신과 마지막 체결 관측의 시각을 분리해 OFF가 과거 체결값으로 되돌아가지 않게 합니다.
          state_updated_at: sensor.projected_at ?? product.projected_at ?? timestamp,
          observed_at: sensor.last_observed_at,
          sensor_state: dto.sensorState,
          sensor_state_name: dto.sensorStateName,
          wind_spd: metrics.windspd,
          wind_spd_ms: metrics.windspd,
          // 서버가 초기 조회와 같은 규칙으로 계산한 값을 실시간 카드에도 전달합니다.
          wind_spd_kt: sensor.wind_spd_kt,
          wind_deg: metrics.winddeg,
          wind_dir: dto.windDir,
          appr_tp: metrics.apprtp,
          calculated_appr_tp: sensor.calculated_appr_tp,
          battery_volt: sensor.battery_volt,
          battery_state: dto.batteryState,
          movement_off_reason: dto.movementOffReason,
          movement_off_reason_name: dto.movementOffReasonName,
          movement_display_label: dto.movementDisplayLabel,
          lat_lon: dto.latLon,
          updated_at: timestamp,
          created_at: timestamp,
          raw_data_text: dto.rawDataText,
        },
      },
    ];
  });
}

/** 확인/종료 갱신은 알림 레이어만 닫으며 제품의 실제 위험 상태는 바꾸지 않습니다. */
export function alarmWidgetMessage(alarm: VersionedRuntimeEntity, product?: VersionedRuntimeEntity, isInitialSnapshot = false): WsBaseMessage {
  const sourceSensor = Array.isArray(product?.sensors) ? product.sensors.find(sensor => sensor.id === alarm.source_product_unit_id) : null;
  // 알람 원본 센서의 같은 현장·제품 연결만 사용하고 모호한 수령인은 장비명으로 유지합니다.
  const matchingProduct = product?.field_uid === alarm.field_uid && (product.id ?? product.entity_id) === alarm.product_instance_id;
  const recipient = matchingProduct && typeof alarm.source_product_unit_id === 'string' ? record(record(product.source_recipients)[alarm.source_product_unit_id]) : {};
  const workerName = readRecipientName(recipient.worker_name);
  const equipmentName = readRecipientName(recipient.equipment_name);
  const recipientName = recipient.assignment_status === 'ASSIGNED' && Boolean(workerName) !== Boolean(equipmentName) ? workerName || equipmentName : null;
  const cleared = alarm.status === 'CLOSED' || alarm.acknowledged_at != null || alarm.status === 'ACKNOWLEDGED';
  const id = String(alarm.id ?? alarm.runtime_key.replace(/^alarm:/, ''));
  const timestamp = String(alarm.closed_at ?? alarm.acknowledged_at ?? alarm.last_presented_received_at ?? alarm.opened_at ?? '');
  return {
    type: WsMessageType.ALARM,
    event: alarm.severity === 'INFO' ? WsEventType.SACON_PRODUCT_INFO : alarm.severity === 'CRITICAL' ? WsEventType.SACON_PRODUCT_ALERT : WsEventType.SACON_PRODUCT_WARNING,
    key: id,
    sent_at: timestamp,
    topic: 'v2.iot.projection',
    field_uid: alarm.field_uid,
    body: {
      ...alarm,
      provider: 'V2',
      is_initial_snapshot: isInitialSnapshot,
      alarm_uid: id,
      event_uid: id,
      event_sequence: alarm.version,
      field_uid: alarm.field_uid,
      source_device_uid: sourceSensor?.source_device_uid ?? alarm.source_product_unit_id,
      device_uid: alarm.product_instance_id,
      display_device_uid: alarm.product_instance_id,
      device_name: product?.name ?? alarm.device_name ?? '',
      notification_display_name: recipientName,
      title: alarm.title,
      content: alarm.content,
      created_at: timestamp,
      receive_at: alarm.last_presented_received_at ?? alarm.opened_at,
      is_check: alarm.acknowledged_at != null,
      ...(cleared ? { cleared_alarm_uid: id } : {}),
    },
  };
}
